import React, { useEffect, useState } from 'react';
import API from '../../pages/Api';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { FaEye, FaEyeSlash } from 'react-icons/fa';

// --- Edit Profile Modal ---
const EditProfileModal = ({ user, onClose }) => {
    const [formData, setFormData] = useState({ 
        name: user.name || '', 
        location: user.location || '',
        mobileno: user.mobileno || '', 
        gender: user.gender || 'Male'
    });

    const handleSubmit = async () => {

        const mobileValue = formData.mobileno || "";

        if (mobileValue.length !== 10) {
            toast.warn("Please enter a valid 10-digit mobile number.");
            return;
        }
        try {
            const token = localStorage.getItem('access_token');
            const dataToSend = { 
                name: formData.name, 
                location: formData.location, 
                gender: formData.gender 
            };
            await API.post('user/update-profile/', formData, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            toast.success("Profile Updated Successfully!");
            onClose();
            window.location.reload();
        } catch (err) { 
            console.error(err); 
            toast.error("Update failed!"); 
        }
    };

    return (
        <div className="fixed inset-0 bg-black/50 flex justify-center items-center z-50 p-4">
            <div className="bg-white p-8 rounded-[40px] w-full max-w-sm shadow-2xl">
                <h2 className="text-xl font-black mb-6 text-gray-800">Edit Profile</h2>
                
                <label className="text-[10px] font-bold text-gray-400 uppercase tracking-widest ml-2">Name</label>
                <input className="w-full p-3 mb-4 border border-gray-200 rounded-xl mt-1" value={formData.name} onChange={(e) => setFormData({...formData, name: e.target.value})} />
                
                <label className="text-[10px] font-bold text-gray-400 uppercase tracking-widest ml-2">Mobile Number</label>
                <input 
                    type="tel" 
                    maxLength="10" 
                    className="w-full p-3 mb-4 border border-gray-200 rounded-xl mt-1" 
                    value={formData.mobileno || ""} 
                    onChange={(e) => setFormData({...formData, mobileno: e.target.value.replace(/\D/g, '')})} 
                />
                
                <label className="text-[10px] font-bold text-gray-400 uppercase tracking-widest ml-2">City</label>
                <input className="w-full p-3 mb-4 border border-gray-200 rounded-xl mt-1" placeholder="e.g. Pune" value={formData.location} onChange={(e) => setFormData({...formData, location: e.target.value})} />

                <label className="text-[10px] font-bold text-gray-400 uppercase tracking-widest ml-2">Gender</label>
                <select className="w-full p-3 mb-6 border border-gray-200 rounded-xl mt-1 bg-white" value={formData.gender} onChange={(e) => setFormData({...formData, gender: e.target.value})}>
                    <option value="1">Male</option>
                    <option value="2">Female</option>
                    <option value="3">Other</option>
                </select>

                <button onClick={handleSubmit} className="w-full bg-[#64a106] text-white py-3 rounded-2xl font-black uppercase tracking-wider">Save Changes</button>
                <button onClick={onClose} className="w-full mt-3 text-gray-400 font-bold hover:text-gray-600">Cancel</button>
            </div>
        </div>
    );
};


// --- Security Modal ---
const SecurityModal = ({ onClose }) => {
   
    const [pass, setPass] = useState({ old_password: '', new_password: '' });
    const [showOld, setShowOld] = useState(false); 
    const [showNew, setShowNew] = useState(false); 

    const handlePasswordChange = async () => {
        try {
            const token = localStorage.getItem('access_token');
            await API.post('user/change-password/', pass, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            toast.success("Password Changed Successfully!");
            onClose();
        } catch (err) { 
            toast.error("Error: Check your old password"); 
        }
    };

    return (
        <div className="fixed inset-0 bg-black/50 flex justify-center items-center z-50 p-4">
            <div className="bg-white p-8 rounded-[40px] w-full max-w-sm shadow-2xl">
                <h2 className="text-xl font-black mb-6">Security & Password</h2>
                
                {/* Old Password Field */}
                <div className="relative mb-4">
                    <input 
                        type={showOld ? "text" : "password"} 
                        className="w-full p-3 border border-gray-200 rounded-xl" 
                        placeholder="Old Password" 
                        onChange={(e) => setPass({...pass, old_password: e.target.value})} 
                    />
                    <button type="button" onClick={() => setShowOld(!showOld)} className="absolute right-4 top-3.5 text-gray-400">
                        {showOld ? <FaEyeSlash /> : <FaEye />}
                    </button>
                </div>

                {/* New Password Field */}
                <div className="relative mb-6">
                    <input 
                        type={showNew ? "text" : "password"} 
                        className="w-full p-3 border border-gray-200 rounded-xl" 
                        placeholder="New Password" 
                        onChange={(e) => setPass({...pass, new_password: e.target.value})} 
                    />
                    <button type="button" onClick={() => setShowNew(!showNew)} className="absolute right-4 top-3.5 text-gray-400">
                        {showNew ? <FaEyeSlash /> : <FaEye />}
                    </button>
                </div>
                
                <button onClick={handlePasswordChange} className="w-full bg-blue-900 text-white py-3 rounded-xl font-bold">Update Password</button>
                <button onClick={onClose} className="w-full mt-3 text-gray-400 font-bold">Cancel</button>
            </div>
        </div>
    );
};

const UserProfile = () => {
    const navigate = useNavigate();
    const [user, setUser] = useState(null);
    const [profilePhoto, setPhoto] = useState(null);
    const [isEditModalOpen, setIsEditModalOpen] = useState(false);
    const [isSecurityModalOpen, setIsSecurityModalOpen] = useState(false);

    useEffect(() => {
        const fetchProfile = async () => {
            try {
                const token = localStorage.getItem("access_token");
                const res = await API.get("user/profile/", {
                    headers: { Authorization: `Bearer ${token}` }
                });
                setUser(res.data);
            } catch (err) { 
                console.error("Error:", err); 
                if (err.response?.status === 401) navigate('/login');
            }
        };
        fetchProfile();
    }, [navigate]);

    const handleProfileUpdate = async () => {
        if (!profilePhoto) return;
        try {
            const token = localStorage.getItem("access_token");
            const formData = new FormData();
            formData.append("profile_photo", profilePhoto);
            await API.post("user/update-profile/", formData, {
                headers: { Authorization: `Bearer ${token}`, "Content-Type": "multipart/form-data" }
            });
            window.location.reload();
        } catch (err) { toast.error("Update failed!"); }
    };

    if (!user) return <div className="p-10 text-center">Loading...</div>;

    return (
        <div className="min-h-screen bg-gray-50 p-4 md:p-8 animate-in fade-in duration-500">
            <div className="max-w-4xl mx-auto mb-6 flex items-center justify-between">
                <button onClick={() => navigate(-1)} className="text-[#64a106] font-bold hover:underline">← Back</button>
                <h1 className="text-xl font-sans text-gray-400 uppercase tracking-[4px]">User Profile</h1>
            </div>

            <div className="max-w-4xl mx-auto grid grid-cols-1 md:grid-cols-3 gap-8">
                
                {/* Left Profile Card */}
                <div className="bg-white rounded-[40px] shadow-2xl p-8 text-center border-b-[10px] border-[#64a106] self-start">
                    <img 
                        src={user.profile_photo ? `${process.env.REACT_APP_BACKEND_URL || 'http://103.172.92.148:8005'}${user.profile_photo}` : `https://ui-avatars.com/api/?name=${user.name}`} 
                        className="w-32 h-32 rounded-full mx-auto mb-4 object-cover border-4" 
                        alt="Profile" 
                    />
                    <h2 className="text-2xl font-black text-gray-800">{user.name}</h2>
                    <p className="text-gray-400 text-sm mb-6">{user.emailid}</p>
                    
                    <label className="cursor-pointer bg-gray-100 px-4 py-2 rounded-xl text-[10px] font-bold uppercase">
                        Change Photo 
                        <input type="file" className="hidden" onChange={(e) => setPhoto(e.target.files[0])} />
                    </label>
                    {profilePhoto && <button onClick={handleProfileUpdate} className="block mt-2 mx-auto bg-[#64a106] text-white px-4 py-2 rounded-xl text-[10px] font-bold">Save Photo</button>}

                    {/* NEW SECTION ADDED HERE */}
                    <div className="text-left space-y-4 pt-6 border-t border-gray-100 mt-6">
                        <div className="flex justify-between">
                            <span className="text-gray-400 font-bold uppercase text-[10px]">Gender</span>
                            <span className="text-gray-800 font-black text-sm">{user.gender || 'N/A'}</span>
                        </div>
                        <div className="flex justify-between">
                            <span className="text-gray-400 font-bold uppercase text-[10px]">City</span>
                            <span className="text-gray-800 font-black text-sm">{user.location || 'N/A'}</span>
                        </div>
                        <div className="flex justify-between">
                            <span className="text-gray-400 font-bold uppercase text-[10px]">Mobile No</span>
                            <span className="text-gray-800 font-black text-sm">{user.mobileno || 'N/A'}</span>
                        </div>
                    </div>
                </div>

                {/* Right Section */}
                <div className="md:col-span-2 space-y-6">
                    <div className="bg-white rounded-[40px] shadow-lg p-8 border-l-[12px] border-blue-900">
                        <h3 className="text-lg font-black uppercase mb-4">📊 Happiness Insights</h3>
                        <div className="grid grid-cols-2 gap-4">
                            <div className="bg-gray-50 p-6 rounded-[30px]"><p className="text-[10px] uppercase font-bold text-gray-400">Score</p><p className="text-3xl font-black text-blue-900">74</p></div>
                            <div className="bg-gray-50 p-6 rounded-[30px]"><p className="text-[10px] uppercase font-bold text-gray-400">Status</p><p className="text-xl font-black text-green-600 uppercase">Healthy</p></div>
                        </div>
                    </div>
                    
                    <div className="bg-white rounded-[40px] shadow-lg overflow-hidden border border-gray-100">
                        <button onClick={() => setIsEditModalOpen(true)} className="w-full flex items-center justify-between p-6 hover:bg-gray-50 transition border-b border-gray-50">
                            <span className="text-sm font-black text-gray-700">Edit Personal Profile</span>
                            <span>→</span>
                        </button>
                        <button onClick={() => setIsSecurityModalOpen(true)} className="w-full flex items-center justify-between p-6 hover:bg-gray-50 transition">
                            <span className="text-sm font-black text-gray-700">Security & Password</span>
                            <span>→</span>
                        </button>
                    </div>
                </div>
            </div>

            {isEditModalOpen && <EditProfileModal user={user} onClose={() => setIsEditModalOpen(false)} />}
            {isSecurityModalOpen && <SecurityModal onClose={() => setIsSecurityModalOpen(false)} />}
        </div>
    );
};

export default UserProfile;